/**
 * Client-side fallback for shortlist coaching (mirrors backend logic).
 */
import { formatFitScoreBadge } from "./matchScoreUtils";

function formatPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : "—";
}

function getMatchPercent(product) {
  const raw = product?.matchScore ?? product?.score ?? product?.match;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Math.round(Number(raw));
}

export function buildShortlistCoachFallback({
  products = [],
  searchParams = null,
  comparisonTable = null,
  topMatchProductId = null,
  valuePickProductId = null,
}) {
  if (!products.length) {
    return { headline: "", guidance: [], source: "fallback" };
  }

  const byId = new Map(products.map((p) => [String(p._id || p.id), p]));
  const top =
    byId.get(String(topMatchProductId)) ||
    products.find((p) => Number(p.rank) === 1) ||
    products[0];
  const value = byId.get(String(valuePickProductId)) || top;
  const topId = String(top._id || top.id);
  const valueId = String(value._id || value.id);
  const isValuePick = topId !== valueId;

  const useCase = searchParams?.useCase
    ? String(searchParams.useCase).toLowerCase()
    : "";
  const useCasePhrase = useCase ? ` for ${useCase}` : "";
  const topScore = getMatchPercent(top);
  const valueScore = getMatchPercent(value);

  const rowsWon = new Map();
  (comparisonTable?.rows || []).forEach((row) => {
    const col = comparisonTable?.columns?.[row.bestIndex];
    if (!col?.id || row.bestIndex == null || row.bestIndex < 0) return;
    const id = String(col.id);
    if (!rowsWon.has(id)) rowsWon.set(id, []);
    rowsWon.get(id).push(row.label || row.key);
  });

  const winsLine = (productId) => {
    const wins = rowsWon.get(String(productId)) || [];
    if (!wins.length) return null;
    return `Leads on ${wins.slice(0, 3).join(", ")}.`;
  };

  let headline;
  if (isValuePick) {
    const topPart =
      topScore != null
        ? `${top.name} scores highest (${formatFitScoreBadge(topScore)})`
        : `${top.name} is your top match by score`;
    const valuePart = `${value.name} is the best value at ${formatPrice(value.price)}`;
    headline = `${topPart}, but ${valuePart}${useCasePhrase}. Compare the trade-offs below before you decide.`;
  } else if (topScore != null) {
    headline = `${top.name} leads your shortlist with a ${formatFitScoreBadge(topScore)}${useCasePhrase} — the strongest overall fit among these options.`;
  } else {
    headline = `${top.name} ranks #1 in your shortlist${useCasePhrase}.`;
  }

  const guidance = [];
  const topWins = winsLine(topId);
  guidance.push({
    productId: topId,
    label: "Top match",
    advice: isValuePick
      ? `Choose ${top.name} if overall fit and specs matter most${topScore != null ? ` (${formatFitScoreBadge(topScore)})` : ""}. ${topWins || ""}`.trim()
      : `${topWins || `Strongest match${useCasePhrase ? ` for your ${useCase} needs` : ""}.`}`.trim(),
  });

  if (isValuePick) {
    const valueWins = winsLine(valueId);
    const budgetMax = searchParams?.budget?.max;
    const under =
      budgetMax != null && Number.isFinite(Number(value.price))
        ? Number(budgetMax) - Number(value.price)
        : null;
    const budgetNote =
      under != null && under >= 0
        ? ` Leaves $${under.toLocaleString()} under your budget.`
        : "";
    guidance.push({
      productId: valueId,
      label: "Best value",
      advice: `Choose ${value.name} if staying within budget is the priority (${formatPrice(value.price)}${valueScore != null ? `, ${formatFitScoreBadge(valueScore)}` : ""}).${budgetNote} ${valueWins || ""}`.trim(),
    });
  }

  products.forEach((p) => {
    const id = String(p._id || p.id);
    if (id === topId || id === valueId) return;
    const wins = winsLine(id);
    if (!wins) return;
    guidance.push({
      productId: id,
      label: null,
      advice: `${p.name}: ${wins}`,
    });
  });

  return {
    headline: headline.slice(0, 480),
    guidance: guidance.map((g) => ({
      ...g,
      advice: String(g.advice || "").slice(0, 280),
    })),
    source: "fallback",
  };
}
